import { AnyStepData, AspectType, Step, StepType } from "../types";
import { WorkflowManager } from "../WorkflowManager";
import { Variant } from "./resource/variant";

/**
 * Steps that support custom color variants should implement the following interface to
 * add the behavior in the way that makes sense to the step.
 */
export interface CustomColorSupport {
    /**
     * Allows for setting a custom color when the custom variant is selected. Will
     * throw when a non-custom variant is selected.
     */
    setCustomColor(color: string);
    /**
     * Get the custom color that is currently set on the step.
     */
    getCustomColor(): string;
}

/**
 * A StepHandle allows for managing the state of a specific step in a workflow. This class
 * abstracts away the complexities of dealing with a step directly and allows for using high level
 * concepts instead of dealing with the underlying data structures.
 */
export abstract class StepHandle<T extends AnyStepData> {
    /**
     * Stores whether or not the step is currently updating.
     */
    private static readonly updateState: Map<string, boolean> = new Map<string, boolean>();
    /**
     * Access to the workflow manager this step is contained by.
     */
    protected readonly manager: WorkflowManager;
    /**
     * The step metadata, useful for determining logic based on configuration.
     */
    protected readonly step: Step<T>;
    /**
     * The tags set against this step.
     */
    protected readonly tags: string[];

    constructor(manager: WorkflowManager, step: Step<T>, tags: string[]) {
        this.manager = manager;
        this.step = step;
        this.tags = tags;
    }

    /**
     * Set the current update state of this step. All step handles pointing to this step will
     * see this value.
     * @param value The new value
     */
    protected setUpdateState(value: boolean) {
        StepHandle.updateState.set(
            `${this.step.stepName}-${this.manager.getWorkflowExperience().getTransaction().id}`,
            value,
        );
    }

    /**
     * @returns Gets the current update state of this step. All step handles for this step will see this value.
     */
    protected getUpdateState() {
        return !!StepHandle.updateState.get(
            `${this.step.stepName}-${this.manager.getWorkflowExperience().getTransaction().id}`,
        );
    }

    /**
     * Gets the currently selected variant, or undefined if no variant is selected.
     */
    getCurrentVariant() {
        const selections = this.manager.getWorkflowSelections();
        const stepSelections = selections[this.step.stepName];
        const variantResource = stepSelections?.selections[0];
        if (!variantResource) return undefined;
        return new Variant(variantResource);
    }

    /**
     * @returns A list of valid variants for this step. Does not include disabled variants.
     */
    getAvailableVariants() {
        const variants = this.step.option?.variants?.filter((v) => v.enabled) || [];
        return variants.map((v) => new Variant(v));
    }

    /**
     * @returns A list of all variants for this step, including disabled ones.
     */
    getAllVariants() {
        const variants = this.step.option?.variants || [];
        return variants.map((v) => new Variant(v));
    }

    /**
     * Most step types have a base option type that variants can be selected for.
     * Selects a specific variant for this step. This will execute all required changes to
     * the design and update the metadata to include the new selection. Any conditions
     * that would be triggered will also be executed.
     */
    abstract selectVariant(variant: Variant): Promise<void>;

    /**
     * @returns A unique identifier for this step within the workflow.
     */
    getId() {
        return this.step.stepName;
    }

    /**
     * @returns The name of the step
     */
    getName(): string {
        return this.step.stepTitle;
    }

    /**
     * @returns A message that can accompany the step name in UI components. Used to describe the purpose of the step in more detail.
     */
    getHelpText() {
        return this.step.helpText;
    }

    /**
     * @returns The type of the step handle.
     */
    getType(): StepType {
        return this.step.type;
    }

    /**
     * @returns The underlying data for this step. Favor using the step handle methods instead of this.
     */
    getRaw(): Step<T> {
        return this.step;
    }

    /**
     * @returns Returns all of the tags that are present on this step.
     */
    getTags(): string[] {
        return this.tags;
    }

    /**
     * @param tag The tag to check for.
     * @returns True if the step has the specified tag, false otherwise.
     */
    hasTag(tag: string): boolean {
        return this.tags.includes(tag) || false;
    }

    /**
     * @returns True if the step is required to be filled by the customer. False otherwise.
     */
    getMandatory() {
        return this.step.mandatory || false;
    }

    /**
     * @param type The AspectType to fetch the value for.
     * @returns A boolean indicating whether this step should override Global Properties.
     * Only relevant when the Workflow Experience is associated with a Bundle that is using Global Properties,
     * and when this step is associated with one or more Global Property Aspects.
     */
    getOverrideGlobalPropertyConfiguration(type: AspectType): boolean {
        const map = this.manager.getStepStorage(this.step.stepName)?.overrideGlobalConfigurations ?? {};
        return map[type.toString()] ?? false;
    }

    /**
     * Sets whether or not this step should override Global Properties.
     * Only relevant when the Workflow Experience is associated with a Bundle that is using Global Properties,
     * and when this step is associated with one or more Global Property Aspects.
     * @param type The AspectType to override.
     */
    setOverrideGlobalPropertyConfiguration(type: AspectType, value: boolean) {
        const map = this.manager.getStepStorage(this.step.stepName)?.overrideGlobalConfigurations ?? {};
        this.manager.updateStorage(this.step.stepName, {
            overrideGlobalConfigurations: { ...map, [type.toString()]: value },
        });
    }

    /**
     * Retrieves the identifiers of all of the configured aspects for the specified Global Property Configuration.
     * @param configurationId The ID of the Global Property Configuration. You can usually find this with `bundle.getGlobalPropertyConfiguration()?.id`
     * @returns An array of strings matching the keys of all the Global Property Aspects in the Configuration that this step is configured to use.
     */
    getGlobalPropertyAspects(configurationId: string) {
        return (
            this.step.globalPropertyAspectConfigurations
                ?.filter((c) => c.globalPropertyConfigurationId === configurationId && c.aspectName)
                .map((c) => c.aspectName!) ?? []
        );
    }

    /**
     * Fires any configured animations on the 3D preview for this step.
     * This includes camera & model animations. If the preview is unavailable
     * this function will do nothing.
     */
    executeAnimations(immediate?: boolean) {
        const preview = this.manager.getPreviewService();
        const modelContainer = this.manager.getModelContainer();
        const modelAnimation = (this.step.data as any).modelAnimation;
        const lookAtAnimation = (this.step.data as any).lookAtAnimation;
        if (preview) {
            lookAtAnimation && preview.executeCameraAnimation(lookAtAnimation);
        } else {
            console.warn("No preview service available, cannot execute camera animations.");
        }
        if (modelContainer) {
            modelAnimation && modelContainer.executeAnimation(modelAnimation, immediate);
        } else {
            console.warn("No model container available, cannot execute model animations.");
        }
    }
}
